import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useStory } from '../../context/StoryContext';
import { useReducedMotion } from '../../hooks/useReducedMotion';

/**
 * CodeReveal — A code block that appears line by line as it scrolls into view
 * Line numbers sit in the same row as their code; indentation is preserved; numbers are not selectable.
 * All lines are in the page from the start (no layout jump, readable without scripts).
 * Respects prefers-reduced-motion by showing everything at once.
 *
 * Importers: src/pages/HomePage.jsx, src/pages/ICEPage.jsx
 * Data schema: Props accept code (string), language ('python' gets highlighting), lineDelay (ms between lines),
 * showLineNumbers (boolean), className (string), style, beatId (string), threshold, rootMargin,
 * onLineComplete (index), onComplete (). charDelay, showCursor and cursorChar are accepted but unused: the typing effect was removed.
 */

const PY_KEYWORDS = new Set(['import', 'from', 'as', 'async', 'await', 'def', 'return', 'class', 'if', 'elif', 'else', 'for', 'in', 'while', 'with', 'try', 'except', 'finally', 'raise', 'lambda', 'pass', 'True', 'False', 'None', 'and', 'or', 'not']);

// Splits one line of Python into comment, string, keyword and plain tokens
const tokenizePython = (line) => {
  const tokens = [];
  let plain = '';
  const flush = () => {
    if (plain) tokens.push({ type: 'plain', text: plain });
    plain = '';
  };
  let i = 0;
  while (i < line.length) {
    const ch = line[i];
    if (ch === '#') {
      flush();
      tokens.push({ type: 'comment', text: line.slice(i) });
      return tokens;
    }
    if (ch === '"' || ch === "'") {
      flush();
      let j = i + 1;
      while (j < line.length && line[j] !== ch) j += line[j] === '\\' ? 2 : 1;
      tokens.push({ type: 'string', text: line.slice(i, j + 1) });
      i = j + 1;
      continue;
    }
    if (/[A-Za-z_]/.test(ch)) {
      let j = i;
      while (j < line.length && /[A-Za-z0-9_]/.test(line[j])) j += 1;
      const word = line.slice(i, j);
      if (PY_KEYWORDS.has(word)) {
        flush();
        tokens.push({ type: 'keyword', text: word });
      } else {
        plain += word;
      }
      i = j;
      continue;
    }
    plain += ch;
    i += 1;
  }
  flush();
  return tokens;
};

const TOKEN_CLASS = {
  keyword: 'text-[#6FB6E8]',
  string: 'text-[#e1dabe]',
  comment: 'text-white/40 italic',
  plain: '',
};

const renderLine = (line, language) => {
  if (language !== 'python') return line;
  return tokenizePython(line).map((t, i) => (
    <span key={i} data-token={t.type} className={TOKEN_CLASS[t.type]}>
      {t.text}
    </span>
  ));
};

// Drop the blank lines a template literal adds at its start and end, keep blank lines inside the code
const trimBlankEnds = (code) => {
  const lines = code.split('\n');
  while (lines.length && lines[0].trim() === '') lines.shift();
  while (lines.length && lines[lines.length - 1].trim() === '') lines.pop();
  return lines;
};

const MAX_REVEAL_MS = 1800;

const CodeReveal = ({
  code = '',
  language = 'python',
  lineDelay = 120,
  showLineNumbers = true,
  className = '',
  style,
  beatId,
  threshold = 0.2,
  rootMargin = '0px',
  onLineComplete,
  onComplete,
  // accepted for older callers; the typing effect was removed
  // eslint-disable-next-line no-unused-vars
  charDelay,
  // eslint-disable-next-line no-unused-vars
  showCursor,
  // eslint-disable-next-line no-unused-vars
  cursorChar,
  ...props
}) => {
  const reducedMotion = useReducedMotion();
  const [state, setState] = useState('idle'); // idle -> revealing -> done
  const rootRef = useRef(null);
  const lines = trimBlankEnds(code);
  const stepSeconds = Math.min(lineDelay, MAX_REVEAL_MS / Math.max(lines.length, 1)) / 1000;

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setState((s) => (s === 'idle' ? (reducedMotion ? 'done' : 'revealing') : s));
          observer.disconnect();
        }
      },
      { threshold, rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [reducedMotion, threshold, rootMargin]);

  const shown = state !== 'idle';

  return (
    <div
      ref={rootRef}
      data-testid="code-reveal"
      data-state={state}
      data-beat-id={beatId}
      className={`font-mono text-[13px] sm:text-sm leading-7 px-6 py-5 overflow-x-auto text-left text-[#FFFFFF]/90 ${className}`}
      style={style}
      {...props}
    >
      {lines.map((line, idx) => (
        <motion.div
          key={idx}
          data-testid="code-line"
          className="flex"
          initial={false}
          animate={{ opacity: shown ? 1 : 0, y: shown ? 0 : 6 }}
          transition={reducedMotion ? { duration: 0 } : { duration: 0.35, delay: idx * stepSeconds, ease: [0.16, 1, 0.3, 1] }}
          onAnimationComplete={() => {
            if (!shown) return;
            if (onLineComplete) onLineComplete(idx);
            if (idx === lines.length - 1) {
              setState('done');
              if (onComplete) onComplete();
            }
          }}
        >
          {showLineNumbers && (
            <span data-testid="code-number" aria-hidden="true" className="w-10 shrink-0 pr-4 text-right select-none text-white/30">
              {idx + 1}
            </span>
          )}
          <span data-testid="code-text" style={{ whiteSpace: 'pre' }}>
            {renderLine(line, language)}
          </span>
        </motion.div>
      ))}
    </div>
  );
};

const CodeBlockReveal = ({ blocks, staggerDelay = 300, className = '', style, beatId, threshold = 0.1, rootMargin = '0px', lineDelay = 200, charDelay = 8, ...props }) => {
  const { reducedMotion } = useStory();
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [containerRef, threshold, rootMargin]);

  if (reducedMotion) {
    return (
      <motion.div
        ref={containerRef}
        className={className}
        style={{ ...style }}
      >
        {isVisible && (
          <>
            {blocks.map((block, index) => (
              <motion.div key={index}>
                <CodeReveal
                  code={block.code}
                  language={block.language}
                  lineDelay={lineDelay}
                  charDelay={charDelay}
                  showLineNumbers={true}
                  showCursor={false}
                  cursorChar="▌"
                  beatId={beatId}
                  threshold={threshold}
                  rootMargin={rootMargin}
                />
              </motion.div>
            ))}
          </>
        )}
      </motion.div>
    );
  }

  return (
    <motion.div
      ref={containerRef}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        y: isVisible ? 0 : 20,
        transition: {
          opacity: { duration: 0.3 },
          y: { duration: 0.5 },
        },
        ...style,
      }}
    >
      {isVisible && (
        <>
          {blocks.map((block, index) => (
            <motion.div key={index}>
              <CodeReveal
                code={block.code}
                language={block.language}
                lineDelay={lineDelay}
                charDelay={charDelay}
                showLineNumbers={true}
                showCursor={false}
                cursorChar="▌"
                beatId={beatId}
                threshold={threshold}
                rootMargin={rootMargin}
              />
            </motion.div>
          ))}
        </>
      )}
    </motion.div>
  );
};

const TypingCursor = ({ char = '▌' }) => {
  // Component removed per user request to eliminate typing animation
  return null;
};

export { CodeReveal, CodeBlockReveal, TypingCursor };
export default CodeReveal;
